import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from "react-select";
import {
  ChevronDown, Search, Plus, ChevronLeft, ChevronRight, X,
  ArrowUpDown, Pencil, Trash2, ArrowRightLeft, Mail, Paperclip, Send, FileSpreadsheet, Users
} from 'lucide-react';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { companiesAPI, clientsAPI, employeesAPI } from "../../../services/api";
import { bulkEmailAPI, templatesAPI } from "../../../services/api";
import { getWhatsAppNumber, getWhatsAppNumberSource } from '../../Templates/templateUtils';
import EmailTemplatePanel from '../../Templates/EmailTemplatePanel';
import WhatsAppTemplatePanel from '../../Templates/WhatsAppTemplatePanel';
import { exportCompaniesToExcel } from "../../../utils/ExportUtils";
import Delete from "../../common/Delete";
import Assign from "../../common/AssignModal";
import Edit from "../../common/Edit";
import CreateCompanyModal from "./CreateCompanyModal";
import ImportCompaniesModal from "./ImportCompaniesModal";
import MigrateToClientModal from "./MigrateToClientModal";
import CompanyEditDrawer from "./CompanyEditDrawer";
import PageNavigator from "../../common/PageNavigator";
import { toast } from "react-toastify";

const BRAND = "#1C4E6D";
const BRAND_DARK = "#153a4f";
const BRAND_LIGHT = "#e8f4f9";
const PAGE_SIZE = 20;

// ── BulkEmailModal — send to selected company IDs ───────────────────────────
const BulkEmailModal = ({ selectedCount, selectedIds, onClose }) => {
  const [subject, setSubject]         = useState('');
  const [body, setBody]               = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending]         = useState(false);
  const [templates, setTemplates]     = useState([]);
  const [showPicker, setShowPicker]   = useState(false);
  const [loadingTpl, setLoadingTpl]   = useState(false);
  const fileRef = useRef();

  const openTemplatePicker = async () => {
    if (showPicker) { setShowPicker(false); return; }
    if (templates.length === 0) {
      setLoadingTpl(true);
      try {
        const res = await templatesAPI.getTemplates(null, 'EMAIL', 'CRM');
        setTemplates(res.data || []);
      } catch { toast.error('Failed to load templates'); }
      finally { setLoadingTpl(false); }
    }
    setShowPicker(true);
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setShowPicker(false);
  };

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim())    { toast.error('Email body is required'); return; }
    setSending(true);
    try {
      const res = await bulkEmailAPI.sendByIds('COMPANY', selectedIds, subject, body, attachments);
      const d = res.data;
      toast.success(`Sent to ${d.successCount}/${d.totalRecipients} recipients`);
      if (d.failedCount > 0) toast.warning(`${d.failedCount} failed`);
      if (d.skippedCount > 0) toast.info(`${d.skippedCount} recipient(s) skipped (do-not-email, invalid email, or limit)`);
      onClose();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: BRAND }}>Bulk Email</p>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Users size={16} className="text-slate-400" />
              Send to {selectedCount} selected {selectedCount === 1 ? 'company' : 'companies'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <button onClick={openTemplatePicker} disabled={loadingTpl}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50">
              {loadingTpl ? 'Loading…' : showPicker ? '▲ Hide templates' : '▼ Use a template'}
            </button>
            {showPicker && (
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                {templates.length === 0 ? (
                  <p className="px-3 py-2.5 text-xs text-slate-400">No email templates found. Create one in My Services → Templates.</p>
                ) : (
                  templates.map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors">
                      <div className="font-semibold text-slate-800">{t.name}</div>
                      {t.subject && <div className="text-slate-400 truncate mt-0.5">{t.subject}</div>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Subject <span className="text-red-500">*</span>
            </label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
              onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${BRAND}40`}
              onBlur={e => e.target.style.boxShadow = ''} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea rows={7} value={body} onChange={e => setBody(e.target.value)}
              placeholder="Write your email message here... (HTML supported)"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 resize-none"
              onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${BRAND}40`}
              onBlur={e => e.target.style.boxShadow = ''} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Attachments <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <div onClick={() => fileRef.current.click()}
              className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer transition-colors"
              onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.backgroundColor = BRAND_LIGHT; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.backgroundColor = ''; }}>
              <Paperclip size={15} className="text-slate-400" />
              <span className="text-sm text-slate-400">Click to attach files</span>
            </div>
            <input ref={fileRef} type="file" multiple className="hidden"
              onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files)])} />
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg border text-xs">
                    <span className="text-slate-600 truncate">{f.name}</span>
                    <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                      className="text-red-400 hover:text-red-600 ml-2"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose} disabled={sending}
            className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSend} disabled={sending}
            className="flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
            {sending
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending…</>
              : <><Send size={14} />Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── ExcelEmailModal — send via excel upload ──────────────────────────────────
const ExcelEmailModal = ({ onClose }) => {
  const [subject, setSubject]         = useState('');
  const [body, setBody]               = useState('');
  const [excelFile, setExcelFile]     = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending]         = useState(false);
  const [templates, setTemplates]     = useState([]);
  const [showPicker, setShowPicker]   = useState(false);
  const [loadingTpl, setLoadingTpl]   = useState(false);
  const excelRef  = useRef();
  const attachRef = useRef();

  const openTemplatePicker = async () => {
    if (showPicker) { setShowPicker(false); return; }
    if (templates.length === 0) {
      setLoadingTpl(true);
      try {
        const res = await templatesAPI.getTemplates(null, 'EMAIL', 'CRM');
        setTemplates(res.data || []);
      } catch { toast.error('Failed to load templates'); }
      finally { setLoadingTpl(false); }
    }
    setShowPicker(true);
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setShowPicker(false);
  };

  const handleSend = async () => {
    if (!excelFile)      { toast.error('Please upload an Excel file'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim())    { toast.error('Email body is required'); return; }
    setSending(true);
    try {
      const res = await bulkEmailAPI.sendByExcel('COMPANY', excelFile, subject, body, attachments);
      const d = res.data;
      toast.success(`Sent to ${d.successCount}/${d.totalRecipients} recipients`);
      if (d.failedCount > 0) toast.warning(`${d.failedCount} failed`);
      if (d.skippedCount > 0) toast.info(`${d.skippedCount} recipient(s) skipped (do-not-email, invalid email, or limit)`);
      if (d.excelRowErrors?.length > 0) toast.info(`${d.excelRowErrors.length} row(s) skipped from Excel (missing/invalid email)`);
      onClose();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: BRAND }}>Excel Email</p>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FileSpreadsheet size={16} className="text-slate-400" />
              Send Email via Excel Upload
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Excel must have an <strong>email</strong> column</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Excel File (.xlsx / .xls) <span className="text-red-500">*</span>
            </label>
            {excelFile ? (
              <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border"
                style={{ backgroundColor: BRAND_LIGHT, borderColor: BRAND }}>
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={15} style={{ color: BRAND }} />
                  <span className="text-sm font-medium truncate" style={{ color: BRAND }}>{excelFile.name}</span>
                </div>
                <button onClick={() => setExcelFile(null)} className="text-red-400 hover:text-red-600 ml-2"><X size={14} /></button>
              </div>
            ) : (
              <div onClick={() => excelRef.current.click()}
                className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer transition-colors"
                onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.backgroundColor = BRAND_LIGHT; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.backgroundColor = ''; }}>
                <FileSpreadsheet size={24} className="text-slate-300" />
                <span className="text-sm text-slate-400">Click to upload Excel file</span>
                <span className="text-xs text-slate-300">.xlsx or .xls only</span>
              </div>
            )}
            <input ref={excelRef} type="file" accept=".xlsx,.xls" className="hidden"
              onChange={e => e.target.files[0] && setExcelFile(e.target.files[0])} />
          </div>
          <div>
            <button onClick={openTemplatePicker} disabled={loadingTpl}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50">
              {loadingTpl ? 'Loading…' : showPicker ? '▲ Hide templates' : '▼ Use a template'}
            </button>
            {showPicker && (
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                {templates.length === 0 ? (
                  <p className="px-3 py-2.5 text-xs text-slate-400">No email templates found. Create one in My Services → Templates.</p>
                ) : (
                  templates.map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t)}
                      className="w-full text-left px-3 py-2.5 text-xs hover:bg-blue-50 border-b border-slate-100 last:border-0 transition-colors">
                      <div className="font-semibold text-slate-800">{t.name}</div>
                      {t.subject && <div className="text-slate-400 truncate mt-0.5">{t.subject}</div>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Subject <span className="text-red-500">*</span>
            </label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none"
              onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${BRAND}40`}
              onBlur={e => e.target.style.boxShadow = ''} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea rows={7} value={body} onChange={e => setBody(e.target.value)}
              placeholder="Write your email message here... (HTML supported)"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none resize-none"
              onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${BRAND}40`}
              onBlur={e => e.target.style.boxShadow = ''} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Attachments <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <div onClick={() => attachRef.current.click()}
              className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer transition-colors"
              onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.backgroundColor = BRAND_LIGHT; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.backgroundColor = ''; }}>
              <Paperclip size={15} className="text-slate-400" />
              <span className="text-sm text-slate-400">Click to attach files</span>
            </div>
            <input ref={attachRef} type="file" multiple className="hidden"
              onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files)])} />
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg border text-xs">
                    <span className="text-slate-600 truncate">{f.name}</span>
                    <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                      className="text-red-400 hover:text-red-600 ml-2"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button onClick={onClose} disabled={sending}
            className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSend} disabled={sending}
            className="flex-1 px-4 py-2.5 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
            {sending
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending…</>
              : <><Send size={14} />Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Company Component ───────────────────────────────────────────────────
const Company = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [migratedCompanyIds, setMigratedCompanyIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState('All companies');
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showExcelEmailModal, setShowExcelEmailModal] = useState(false);
  const [selectedCompaniesForEdit, setSelectedCompaniesForEdit] = useState([]);
  const [selectedCompaniesForMigrate, setSelectedCompaniesForMigrate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [singleDeleteTarget, setSingleDeleteTarget] = useState(null);
  const [editDrawerCompany, setEditDrawerCompany] = useState(null);
  const [employeeNameCache, setEmployeeNameCache] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [emailPanelCompany, setEmailPanelCompany] = useState(null);
  const [whatsappPanelCompany, setWhatsappPanelCompany] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('companyViewColumns');
    return saved ? JSON.parse(saved) : [
      'companyName', 'websiteUrl', 'industry', 'relationshipType',
      'leadStatus', 'leadSource', 'primaryContact', 'email',
      'phone', 'contactOwner', 'createdAt'
    ];
  });

  const allColumns = [
    { key: 'companyName', label: 'Company Name', required: true },
    { key: 'websiteUrl', label: 'Website' },
    { key: 'industry', label: 'Industry' },
    { key: 'relationshipType', label: 'Relationship Type' },
    { key: 'leadStatus', label: 'Lead Status' },
    { key: 'leadSource', label: 'Lead Source' },
    { key: 'linkedInUrl', label: 'LinkedIn' },
    { key: 'companyStage', label: 'Company Stage' },
    { key: 'legalType', label: 'Legal Type' },
    { key: 'employeeCount', label: 'Employee Count' },
    { key: 'annualRevenue', label: 'Annual Revenue' },
    { key: 'primaryContact', label: 'Primary Contact' },
    { key: 'contact_ids', label: 'Contact IDs' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'contactOwner', label: 'Assigned To' },
    { key: 'createdAt', label: 'Create Date' },
    { key: 'lastActivity', label: 'Last Activity Date' }
  ];

  const [filters, setFilters] = useState({
    industry: null, leadStatus: null, leadSource: null, relationshipType: null,
    companyStage: null, legalType: null, employeeCount: null, annualRevenue: null,
    companyName: '', websiteUrl: '', email: '', phone: '',
    primaryContact: '', jobTitle: '', contactOwner: null
  });

  const tabs = ['All companies', 'My companies'];

  const industryOptions = [
    { value: 'IT', label: 'IT' }, { value: 'FINANCE', label: 'Finance' },
    { value: 'HEALTHCARE', label: 'Healthcare' }, { value: 'EDUCATION', label: 'Education' },
    { value: 'MANUFACTURING', label: 'Manufacturing' }, { value: 'RETAIL', label: 'Retail' },
    { value: 'OTHER', label: 'Other' }
  ];
  const leadStatusOptions = [
    { value: 'NEW', label: 'New' }, { value: 'CONTACTED', label: 'Contacted' },
    { value: 'QUALIFIED', label: 'Qualified' }, { value: 'LOST', label: 'Lost' },
    { value: 'WON', label: 'Won' }, { value: 'OTHER', label: 'Other' }
  ];
  const leadSourceOptions = [
    { value: 'WEBSITE', label: 'Website' }, { value: 'REFERRAL', label: 'Referral' },
    { value: 'PARTNER', label: 'Partner' }, { value: 'SOCIAL_MEDIA', label: 'Social Media' },
    { value: 'EMAIL', label: 'Email' }, { value: 'CALL', label: 'Call' }, { value: 'OTHER', label: 'Other' }
  ];
  const relationshipTypeOptions = [
    { value: 'CLIENT', label: 'Client' }, { value: 'PARTNER', label: 'Partner' },
    { value: 'SUPPLIER', label: 'Supplier' }, { value: 'VENDOR', label: 'Vendor' },
    { value: 'DISTRIBUTOR', label: 'Distributor' }, { value: 'RESELLER', label: 'Reseller' },
    { value: 'CONTRACTOR', label: 'Contractor' }, { value: 'CONSULTANT', label: 'Consultant' },
    { value: 'SERVICE_PROVIDER', label: 'Service Provider' }, { value: 'INVESTOR', label: 'Investor' },
    { value: 'MANUFACTURER', label: 'Manufacturer' }, { value: 'OTHER', label: 'Other' }
  ];
  const companyStageOptions = [
    { value: 'STARTUP', label: 'Startup' }, { value: 'SMALL', label: 'Small' },
    { value: 'MEDIUM', label: 'Medium' }, { value: 'LARGE', label: 'Large' },
    { value: 'ENTERPRISE', label: 'Enterprise' }, { value: 'OTHER', label: 'Other' }
  ];
  const legalTypeOptions = [
    { value: 'SOLE_PROPRIETORSHIP', label: 'Sole Proprietorship' },
    { value: 'PARTNERSHIP_FIRM', label: 'Partnership Firm' }, { value: 'LLP', label: 'LLP' },
    { value: 'PRIVATE_LIMITED', label: 'Private Limited' }, { value: 'PUBLIC_LIMITED', label: 'Public Limited' },
    { value: 'OPC', label: 'One Person Company' }, { value: 'JOINT_VENTURE', label: 'Joint Venture' },
    { value: 'NON_PROFIT', label: 'Non-Profit' }, { value: 'GOVERNMENT_ENTITY', label: 'Government Entity' },
    { value: 'OTHER', label: 'Other' }
  ];
  const employeeCountOptions = [
    { value: 'RANGE_1_10', label: '1-10' }, { value: 'RANGE_11_50', label: '11-50' },
    { value: 'RANGE_51_100', label: '51-100' }, { value: 'RANGE_101_250', label: '101-250' },
    { value: 'RANGE_251_500', label: '251-500' }, { value: 'RANGE_501_1000', label: '501-1000' },
    { value: 'RANGE_1001_5000', label: '1001-5000' }, { value: 'RANGE_5001_10000', label: '5001-10000' },
    { value: 'RANGE_10000_PLUS', label: '10,000+' }
  ];
  const annualRevenueOptions = [
    { value: 'BELOW_1M', label: 'Below $1M' }, { value: 'RANGE_1M_5M', label: '$1M - $5M' },
    { value: 'RANGE_5M_10M', label: '$5M - $10M' }, { value: 'RANGE_10M_50M', label: '$10M - $50M' },
    { value: 'RANGE_50M_100M', label: '$50M - $100M' }, { value: 'RANGE_100M_500M', label: '$100M - $500M' },
    { value: 'RANGE_500M_1B', label: '$500M - $1B' }, { value: 'RANGE_1B_5B', label: '$1B - $5B' },
    { value: 'RANGE_5B_10B', label: '$5B - $10B' }, { value: 'ABOVE_10B', label: 'Above $10B' }
  ];

  useEffect(() => {
    const ids = [...new Set(companies.map(c => c.contactOwner).filter(Boolean))];
    const uncachedIds = ids.filter(id => !(id in employeeNameCache));
    if (uncachedIds.length === 0) return;
    uncachedIds.forEach(async (id) => {
      try {
        const response = await employeesAPI.getEmployeeById(id);
        const emp = response.data;
        const name = emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || String(id);
        setEmployeeNameCache(prev => ({ ...prev, [id]: name }));
      } catch { setEmployeeNameCache(prev => ({ ...prev, [id]: String(id) })); }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies]);

  const fetchMigratedIds = async () => {
    try {
      const response = await clientsAPI.getMigratedIds();
      setMigratedCompanyIds(new Set(response.data || []));
    } catch (error) { console.error('Failed to fetch migrated ids:', error); }
  };

  const fetchEmployeeOptions = async () => {
    try {
      const response = await employeesAPI.getAllEmployees();
      const options = (response.data || []).map(emp => ({
        value: emp.id,
        label: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || emp.id
      }));
      setEmployeeOptions(options);
    } catch (error) { console.error('Failed to fetch employees:', error); }
  };

  const fetchCompaniesPaged = async (page) => {
    setLoading(true);
    try {
      const params = {
        page, size: PAGE_SIZE,
        search: filters.companyName || undefined,
        industry: filters.industry?.value,
        leadStatus: filters.leadStatus?.value,
        leadSource: filters.leadSource?.value,
        relationshipType: filters.relationshipType?.value,
        companyStage: filters.companyStage?.value,
        legalType: filters.legalType?.value,
        employeeCount: filters.employeeCount?.value,
        annualRevenue: filters.annualRevenue?.value,
        email: filters.email || undefined,
        phone: filters.phone || undefined,
        websiteUrl: filters.websiteUrl || undefined,
        contactOwner: filters.contactOwner?.value,
        primaryContact: filters.primaryContact || undefined,
        jobTitle: filters.jobTitle || undefined,
        ...(activeTab === 'My companies' ? { scope: 'mine' } : {})
      };
      const response = await companiesAPI.getCompaniesPaged(params);
      setCompanies(response.data.content || []);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
    } catch (error) { toast.error("Failed to fetch companies"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMigratedIds(); fetchEmployeeOptions(); }, []);

  useEffect(() => {
    fetchCompaniesPaged(currentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab, currentPage]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(0);
  };
  const clearAllFilters = () => {
    setFilters({
      industry: null, leadStatus: null, leadSource: null, relationshipType: null,
      companyStage: null, legalType: null, employeeCount: null, annualRevenue: null,
      companyName: '', websiteUrl: '', email: '', phone: '',
      primaryContact: '', jobTitle: '', contactOwner: null
    });
    setCurrentPage(0);
  };

  const handleRowSelect = (id) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  const handleSelectAll = () => setSelectedRows(selectedRows.length === companies.length ? [] : companies.map(c => c.id));

  const formatValue = (key, value) => {
    if (!value) return '—';
    if (key === 'employeeCount') return value.replace('RANGE_', '').replace(/_/g, '-');
    if (key === 'annualRevenue') return value.replace('RANGE_', '$').replace(/_/g, '-');
    if (key === 'legalType') return value.replace(/_/g, ' ');
    return value;
  };

  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev => {
      const n = prev.includes(columnKey) ? prev.filter(k => k !== columnKey) : [...prev, columnKey];
      localStorage.setItem('companyViewColumns', JSON.stringify(n));
      return n;
    });
  };

  const isColumnVisible = (k) => visibleColumns.includes(k);
  const getSelectedCompanyObjects = () => companies.filter(c => selectedRows.includes(c.id));
  const getNonMigratedSelected = () => getSelectedCompanyObjects().filter(c => !migratedCompanyIds.has(c.id));

  const handleSingleDelete = async (company) => { setSingleDeleteTarget(company); setShowDeleteModal(true); };

  const enumFields = {
    industry: industryOptions, leadStatus: leadStatusOptions, leadSource: leadSourceOptions,
    relationshipType: relationshipTypeOptions, companyStage: companyStageOptions,
    legalType: legalTypeOptions, employeeCount: employeeCountOptions, annualRevenue: annualRevenueOptions
  };
  const getOptionsForColumn = (columnKey) => enumFields[columnKey] || null;
  const isEnumField = (columnKey) => columnKey in enumFields;

  const handleCellDoubleClick = (company, columnKey) => {
    if (['companyName', 'id', 'createdAt', 'updatedAt'].includes(columnKey)) return;
    setEditingCell({ rowId: company.id, columnKey });
    setEditingValue(company[columnKey] || '');
  };
  const handleCellInputChange = (e) => setEditingValue(e.target.value);
  const handleCellSelectChange = (value) => setEditingValue(value);

  const handleCellSave = async () => {
    if (!editingCell) return;
    try {
      const company = companies.find(c => c.id === editingCell.rowId);
      if (!company) return;
      await companiesAPI.updateCompany(company.id, { [editingCell.columnKey]: editingValue });
      setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, [editingCell.columnKey]: editingValue } : c));
      toast.success('Field updated successfully');
      setEditingCell(null); setEditingValue('');
    } catch (error) {
      toast.error('Failed to update field: ' + (error.response?.data?.message || error.message));
      setEditingCell(null);
    }
  };
  const handleCellCancel = () => { setEditingCell(null); setEditingValue(''); };
  const isEditingCell = (companyId, columnKey) => editingCell?.rowId === companyId && editingCell?.columnKey === columnKey;

  // ── FIX: All null/undefined values handled safely ────────────────────────
  const renderCellContent = (column, company) => {
    switch (column.key) {

      // ── FIX 1: companyName can be null from API ──
      case 'companyName': {
        const cName = company.companyName || '';
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ backgroundColor: BRAND }}
            >
              {cName ? cName.charAt(0).toUpperCase() : '?'}
            </div>
            <button
              onClick={() => navigate(`/company/${company.id}`)}
              className="text-sm font-medium hover:underline cursor-pointer"
              style={{ color: BRAND }}
              onMouseEnter={e => e.currentTarget.style.color = BRAND_DARK}
              onMouseLeave={e => e.currentTarget.style.color = BRAND}
            >
              {cName || <span className="text-gray-400 italic">Unnamed</span>}
            </button>
            {migratedCompanyIds.has(company.id) && (
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium whitespace-nowrap">
                Client &#10003;
              </span>
            )}
          </div>
        );
      }

      case 'websiteUrl':
        return company.websiteUrl ? (
          <a href={`https://${company.websiteUrl}`} target="_blank" rel="noopener noreferrer"
            className="hover:underline" style={{ color: BRAND }}>{company.websiteUrl}</a>
        ) : <span className="text-gray-400">—</span>;

      case 'relationshipType':
      case 'leadStatus':
      case 'leadSource':
      case 'companyStage':
        return company[column.key] ? (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-white border">
            {company[column.key].replace(/_/g, ' ')}
          </span>
        ) : <span className="text-gray-400">—</span>;

      case 'linkedInUrl':
        return company.linkedInUrl ? (
          <a href={company.linkedInUrl} target="_blank" rel="noopener noreferrer"
            className="hover:underline" style={{ color: BRAND }}>View Profile</a>
        ) : <span className="text-gray-400">—</span>;

      case 'legalType':
      case 'employeeCount':
      case 'annualRevenue':
        return <span>{formatValue(column.key, company[column.key])}</span>;

      case 'email':
        return company.email ? (
          <div className="flex items-center gap-1.5">
            <a href={`mailto:${company.email}`} className="hover:underline" style={{ color: BRAND }}>{company.email}</a>
            <button onClick={e => { e.stopPropagation(); setEmailPanelCompany(company); }} title="Send email from template"
              className="opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-blue-50 cursor-pointer" style={{ color: BRAND }}>
              <Mail size={13} />
            </button>
          </div>
        ) : <span className="text-gray-400">—</span>;

      case 'phone':
        return company.phone ? (
          <div className="flex items-center gap-1.5">
            <a href={`tel:${company.phone}`} className="hover:underline" style={{ color: BRAND }}>{company.phone}</a>
            <button onClick={e => { e.stopPropagation(); if (getWhatsAppNumber(company)) setWhatsappPanelCompany(company); }}
              title={getWhatsAppNumber(company) ? 'Send WhatsApp' : 'Not a valid WhatsApp number'}
              disabled={!getWhatsAppNumber(company)}
              className={`opacity-40 group-hover:opacity-100 transition-opacity p-0.5 rounded ${getWhatsAppNumber(company) ? 'text-[#25D366] hover:bg-green-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'}`}>
              <IoLogoWhatsapp size={13} />
            </button>
          </div>
        ) : <span className="text-gray-400">—</span>;

      // ── FIX 2: contactOwner name can be empty string ──
      case 'contactOwner': {
        const ownerId = company.contactOwner;
        if (!ownerId) return <span className="text-gray-400">—</span>;
        const ownerName = employeeNameCache[ownerId];
        if (ownerName === undefined) {
          return (
            <span className="flex items-center gap-1 text-gray-400 text-xs">
              <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />
              Loading...
            </span>
          );
        }
        const displayName = ownerName || String(ownerId);
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${BRAND}, #2a6a9f)` }}
            >
              {displayName ? displayName.charAt(0).toUpperCase() : '?'}
            </div>
            <span className="text-sm text-gray-800">{displayName}</span>
          </div>
        );
      }

      case 'createdAt':
        return company.createdAt
          ? <span>{new Date(company.createdAt).toLocaleDateString()}</span>
          : <span className="text-gray-400">—</span>;

      case 'lastActivity':
        return company.lastActivity
          ? <span>{new Date(company.lastActivity).toLocaleDateString()}</span>
          : <span className="text-gray-400">—</span>;

      // ── FIX 3: default case — null/undefined safe ──
      default: {
        const val = company[column.key];
        return val !== null && val !== undefined
          ? <span>{String(val)}</span>
          : <span className="text-gray-400">—</span>;
      }
    }
  };

  return (
    <div>
      {showBulkEmailModal && (
        <BulkEmailModal selectedCount={selectedRows.length} selectedIds={selectedRows} onClose={() => setShowBulkEmailModal(false)} />
      )}
      {showExcelEmailModal && (
        <ExcelEmailModal onClose={() => setShowExcelEmailModal(false)} />
      )}

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 bg-white border-b gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 flex items-center gap-2">
            Companies <ChevronDown size={20} className="text-gray-500" />
          </h1>
          <span className="text-xs sm:text-sm text-gray-600">{totalElements} records</span>
          {loading && <span className="text-xs sm:text-sm" style={{ color: BRAND }}>Loading...</span>}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button onClick={() => setShowExcelEmailModal(true)}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm text-white rounded-md whitespace-nowrap transition-colors"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
            Bulk Email
          </button>
          <button onClick={() => setShowImportModal(true)}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm text-white rounded-md whitespace-nowrap transition-colors"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
            Import
          </button>
          <button onClick={() => setShowCreateModal(true)}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm text-white rounded-md whitespace-nowrap transition-colors"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
            Create
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center border-b overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); setSelectedRows([]); setCurrentPage(0); }}
            className={`px-4 sm:px-6 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab ? 'text-gray-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            style={activeTab === tab ? { borderBottomColor: BRAND, color: BRAND } : {}}>
            {tab}
            {tab === 'All companies' && activeTab === tab && <X className="ml-2 inline-block w-4 h-4 text-gray-400" />}
          </button>
        ))}
        <div className="ml-auto px-4 sm:px-6">
          <button onClick={() => setShowColumnSelector(true)}
            className="text-xs sm:text-sm hover:underline whitespace-nowrap" style={{ color: BRAND }}>
            <span className="hidden sm:inline">Customize Columns</span>
            <span className="sm:hidden">Columns</span>
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-48"><Select options={industryOptions} value={filters.industry} onChange={v => handleFilterChange('industry', v)} placeholder="Industry" isClearable className="text-sm" /></div>
          <div className="w-48"><Select options={leadStatusOptions} value={filters.leadStatus} onChange={v => handleFilterChange('leadStatus', v)} placeholder="Lead Status" isClearable className="text-sm" /></div>
          <div className="w-48"><Select options={leadSourceOptions} value={filters.leadSource} onChange={v => handleFilterChange('leadSource', v)} placeholder="Lead Source" isClearable className="text-sm" /></div>
          <button onClick={() => setShowMoreFilters(!showMoreFilters)}
            className="flex items-center gap-1 px-3 py-2 text-sm rounded-md border"
            style={{ color: BRAND, borderColor: BRAND }}>
            <Plus size={16} /> More Filters
          </button>
          {(filters.industry || filters.leadStatus || filters.leadSource || filters.companyName ||
            filters.relationshipType || filters.companyStage || filters.legalType || filters.employeeCount || filters.annualRevenue) && (
            <button onClick={clearAllFilters} className="px-3 py-2 text-sm text-red-600 border border-red-300 rounded-md">Clear All</button>
          )}
        </div>
        {showMoreFilters && (
          <div className="grid grid-cols-4 gap-3 mt-4 p-4 bg-white rounded-md border">
            <div><label className="block text-xs text-gray-600 mb-1">Relationship Type</label><Select options={relationshipTypeOptions} value={filters.relationshipType} onChange={v => handleFilterChange('relationshipType', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Company Stage</label><Select options={companyStageOptions} value={filters.companyStage} onChange={v => handleFilterChange('companyStage', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Legal Type</label><Select options={legalTypeOptions} value={filters.legalType} onChange={v => handleFilterChange('legalType', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Employee Count</label><Select options={employeeCountOptions} value={filters.employeeCount} onChange={v => handleFilterChange('employeeCount', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div><label className="block text-xs text-gray-600 mb-1">Annual Revenue</label><Select options={annualRevenueOptions} value={filters.annualRevenue} onChange={v => handleFilterChange('annualRevenue', v)} placeholder="Select..." isClearable className="text-sm" /></div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Assigned To</label>
              <Select options={employeeOptions} value={filters.contactOwner} onChange={v => handleFilterChange('contactOwner', v)} placeholder="Assigned To" isClearable className="text-sm" />
            </div>
            {[['companyName','Company Name','Search name...'],['websiteUrl','Website','Search website...'],['email','Email','Search email...'],['phone','Phone','Search phone...'],['primaryContact','Primary Contact','Search contact...'],['jobTitle','Job Title','Search title...']].map(([key,label,ph]) => (
              <div key={key}>
                <label className="block text-xs text-gray-600 mb-1">{label}</label>
                <input type="text" value={filters[key]} onChange={e => handleFilterChange(key, e.target.value)} placeholder={ph}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none"
                  onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${BRAND}40`}
                  onBlur={e => e.target.style.boxShadow = ''} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Search and Bulk Actions ── */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-4 border-b gap-4">
        <div className="relative flex-1 max-w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input type="text" placeholder="Search name, phone, or domain" value={filters.companyName}
            onChange={e => handleFilterChange('companyName', e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none text-sm"
            onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${BRAND}40`}
            onBlur={e => e.target.style.boxShadow = ''} />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
          <button onClick={() => exportCompaniesToExcel(companies)}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-semibold">
            Export
          </button>
          <button onClick={() => {
              if (!selectedRows.length) { alert("Please select at least one company to edit"); return; }
              setSelectedCompaniesForEdit(getSelectedCompanyObjects());
              setShowEditModal(true);
            }}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600 whitespace-nowrap">
            Bulk Edit
          </button>
          <button onClick={() => { if (selectedRows.length) { setSingleDeleteTarget(null); setShowDeleteModal(true); } else alert("Please select at least one company to delete"); }}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-red-500 text-white rounded-md hover:bg-red-600 whitespace-nowrap">
            Bulk Delete
          </button>
          <button onClick={() => { if (selectedRows.length) setShowAssignModal(true); else alert("Please select at least one company to assign"); }}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-purple-500 text-white rounded-md hover:bg-purple-600 whitespace-nowrap">
            Assign
          </button>
          {selectedRows.length > 0 && (
            <button onClick={() => setShowBulkEmailModal(true)}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs sm:text-sm text-white rounded-md whitespace-nowrap transition-colors"
              style={{ backgroundColor: BRAND }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
              <Mail size={14} /> Send Email ({selectedRows.length})
            </button>
          )}
          {selectedRows.length > 0 && getNonMigratedSelected().length > 0 && (
            <button onClick={() => { setSelectedCompaniesForMigrate(getNonMigratedSelected()); setShowMigrateModal(true); }}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-white rounded-md whitespace-nowrap transition-colors"
              style={{ backgroundColor: '#4f46e5' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#4338ca'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#4f46e5'}>
              Migrate to Client
              {getNonMigratedSelected().length < selectedRows.length && ` (${getNonMigratedSelected().length})`}
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="overflow-x-auto bg-white">
        <table className="w-full min-w-max">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="w-12 p-3 bg-gray-50">
                <input type="checkbox"
                  checked={selectedRows.length === companies.length && companies.length > 0}
                  onChange={handleSelectAll} className="w-4 h-4 rounded border-gray-300"
                  style={{ accentColor: BRAND }} />
              </th>
              {allColumns.filter(col => isColumnVisible(col.key)).map(column => (
                <th key={column.key} className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                  <div className="flex items-center gap-2">{column.label}<ArrowUpDown size={12} className="text-gray-400" /></div>
                </th>
              ))}
              <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {companies.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 2} className="p-8 text-center text-gray-500">
                  {loading ? 'Loading companies...' : 'No companies found'}
                </td>
              </tr>
            ) : (
              companies.map(company => {
                const isMigrated = migratedCompanyIds.has(company.id);
                const isSelected = selectedRows.includes(company.id);
                return (
                  <tr key={company.id}
                    onMouseEnter={() => setHoveredRow(company.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className="group hover:bg-gray-50"
                    style={{ backgroundColor: isSelected ? BRAND_LIGHT : '' }}>
                    <td className="p-3">
                      <input type="checkbox" checked={isSelected}
                        onChange={() => handleRowSelect(company.id)}
                        className="w-4 h-4 rounded border-gray-300" style={{ accentColor: BRAND }} />
                    </td>
                    {allColumns.filter(col => isColumnVisible(col.key)).map(column => {
                      const isEnum = isEnumField(column.key);
                      const options = getOptionsForColumn(column.key);
                      return (
                        <td key={column.key} className="p-3 text-xs cursor-text transition-colors"
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = BRAND_LIGHT; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = ''; }}
                          onDoubleClick={() => handleCellDoubleClick(company, column.key)}>
                          {isEditingCell(company.id, column.key) ? (
                            <div className="flex items-center gap-1">
                              {isEnum ? (
                                <select value={editingValue} onChange={e => handleCellSelectChange(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') handleCellCancel(); }}
                                  className="flex-1 px-2 py-1 border rounded bg-white text-xs focus:outline-none"
                                  style={{ borderColor: BRAND, boxShadow: `0 0 0 2px ${BRAND}40` }} autoFocus>
                                  <option value="">Select {column.label}</option>
                                  {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                              ) : (
                                <input type="text" value={editingValue} onChange={handleCellInputChange}
                                  onKeyDown={e => { if (e.key === 'Enter') handleCellSave(); if (e.key === 'Escape') handleCellCancel(); }}
                                  className="flex-1 px-2 py-1 border rounded bg-white text-xs focus:outline-none"
                                  style={{ borderColor: BRAND, boxShadow: `0 0 0 2px ${BRAND}40` }} autoFocus />
                              )}
                              <button onClick={handleCellSave} className="text-green-600 hover:text-green-700 font-bold text-xs" title="Save (Enter)">&#10003;</button>
                              <button onClick={handleCellCancel} className="text-red-600 hover:text-red-700 font-bold text-xs" title="Cancel (Esc)">&#x2715;</button>
                            </div>
                          ) : (
                            renderCellContent(column, company)
                          )}
                        </td>
                      );
                    })}
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <button onClick={e => { e.stopPropagation(); setEditDrawerCompany(company); }}
                          className="p-1.5 rounded-md hover:bg-yellow-50 text-yellow-500 hover:text-yellow-600 transition-colors" title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button onClick={e => { e.stopPropagation(); handleSingleDelete(company); }}
                          className="p-1.5 rounded-md hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title="Delete">
                          <Trash2 size={14} />
                        </button>
                        {!isMigrated && (
                          <button onClick={e => { e.stopPropagation(); setSelectedCompaniesForMigrate([company]); setShowMigrateModal(true); }}
                            className="p-1.5 rounded-md hover:bg-indigo-50 text-indigo-400 hover:text-indigo-600 transition-colors" title="Migrate to Client">
                            <ArrowRightLeft size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <PageNavigator
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
      />

      {/* ── Modals ── */}
      {showDeleteModal && (
        <Delete count={singleDeleteTarget ? 1 : selectedRows.length}
          onClose={() => { setShowDeleteModal(false); setSingleDeleteTarget(null); }}
          entityName="company"
          onConfirm={async () => {
            try {
              if (singleDeleteTarget) {
                await companiesAPI.deleteCompany(singleDeleteTarget.id);
                setCompanies(prev => prev.filter(c => c.id !== singleDeleteTarget.id));
                toast.success("Company deleted successfully");
              } else {
                const res = await companiesAPI.bulkDelete(selectedRows);
                const r = res.data || {};
                const failedIds = new Set((r.errors || []).map(e => e.id));
                setCompanies(prev => prev.filter(c => !(selectedRows.includes(c.id) && !failedIds.has(c.id))));
                setSelectedRows([]);
                if (r.failedCount > 0) toast.warning(`${r.successCount} deleted, ${r.failedCount} could not be deleted`);
                else toast.success(`${r.successCount ?? selectedRows.length} companies deleted successfully`);
              }
              setSingleDeleteTarget(null); setShowDeleteModal(false);
            } catch (error) {
              toast.error(error.response?.data?.message || (error.response?.status === 401 ? "Unauthorized" : "Failed to delete"));
            }
          }}
        />
      )}

      {showAssignModal && (
        <Assign selectedCount={selectedRows.length} onClose={() => setShowAssignModal(false)}
          onConfirmAssign={async (value) => {
            try {
              await companiesAPI.updateContactOwner({ ids: selectedRows, contactOwner: value });
              toast.success("Contact owner updated");
              setCompanies(prev => prev.map(c => selectedRows.includes(c.id) ? { ...c, contactOwner: value } : c));
              setShowAssignModal(false);
            } catch (error) { toast.error("Failed to update contact owner"); }
          }}
        />
      )}

      {showEditModal && (
        <Edit selectedCount={selectedCompaniesForEdit.length} selectedCompanies={selectedCompaniesForEdit}
          onClose={() => setShowEditModal(false)}
          onSave={async (updatedData) => {
            try {
              const fieldName = Object.keys(updatedData)[0];
              const fieldValue = updatedData[fieldName];
              const ids = selectedCompaniesForEdit.map(c => c.id);
              const res = await companiesAPI.bulkEdit({ ids, fieldName, fieldValue });
              const r = res.data || {};
              const failedIds = new Set((r.errors || []).map(e => e.id));
              if (r.failedCount > 0) toast.warning(`${r.successCount} updated, ${r.failedCount} failed`);
              else toast.success(`${r.successCount ?? ids.length} companies updated`);
              setCompanies(prev => prev.map(c => ids.includes(c.id) && !failedIds.has(c.id) ? { ...c, [fieldName]: fieldValue } : c));
              setShowEditModal(false);
            } catch (error) { toast.error(error.response?.data?.message || "Failed to update"); }
          }}
        />
      )}

      {editDrawerCompany && (
        <CompanyEditDrawer company={editDrawerCompany} onClose={() => setEditDrawerCompany(null)}
          onSave={(updatedCompany) => {
            setCompanies(prev => prev.map(c => c.id === updatedCompany.id ? updatedCompany : c));
            setEditDrawerCompany(null);
          }}
        />
      )}

      {showMigrateModal && (
        <MigrateToClientModal selectedCompanies={selectedCompaniesForMigrate}
          onClose={() => { setShowMigrateModal(false); setSelectedCompaniesForMigrate([]); }}
          onMigrate={() => {
            setSelectedRows([]);
            fetchMigratedIds();
            fetchCompaniesPaged(currentPage);
          }}
        />
      )}

      {showColumnSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Edit columns</h3>
              <button onClick={() => setShowColumnSelector(false)}><X size={20} /></button>
            </div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <button onClick={() => { const k = allColumns.map(c => c.key); setVisibleColumns(k); localStorage.setItem('companyViewColumns', JSON.stringify(k)); }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Select all</button>
                <button onClick={() => { const k = allColumns.filter(c => c.required).map(c => c.key); setVisibleColumns(k); localStorage.setItem('companyViewColumns', JSON.stringify(k)); }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Deselect all</button>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allColumns.map(column => (
                  <label key={column.key} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input type="checkbox" checked={isColumnVisible(column.key)}
                      onChange={() => !column.required && handleColumnToggle(column.key)}
                      disabled={column.required} className="rounded border-gray-300"
                      style={{ accentColor: BRAND }} />
                    <span className="text-sm">{column.label}</span>
                    {column.required && <span className="text-xs text-gray-500">(Required)</span>}
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button onClick={() => setShowColumnSelector(false)}
                className="px-4 py-2 text-sm text-white rounded-md transition-colors"
                style={{ backgroundColor: BRAND }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = BRAND_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = BRAND}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateCompanyModal onClose={() => setShowCreateModal(false)}
          onCreate={() => fetchCompaniesPaged(currentPage)} />
      )}

      {showImportModal && (
        <ImportCompaniesModal onClose={() => setShowImportModal(false)}
          onImport={() => fetchCompaniesPaged(currentPage)} />
      )}

      <EmailTemplatePanel
        isOpen={Boolean(emailPanelCompany)}
        onClose={() => setEmailPanelCompany(null)}
        module="CRM"
        context={emailPanelCompany ? {
          companyName: emailPanelCompany.companyName || '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={emailPanelCompany ? {
          email: emailPanelCompany.primaryContactEmail || emailPanelCompany.email,
          name: emailPanelCompany.companyName || '',
        } : null}
      />
      <WhatsAppTemplatePanel
        isOpen={Boolean(whatsappPanelCompany)}
        onClose={() => setWhatsappPanelCompany(null)}
        module="CRM"
        context={whatsappPanelCompany ? {
          companyName: whatsappPanelCompany.companyName || '',
          senderName: localStorage.getItem('email') || '',
          agencyName: localStorage.getItem('organizationName') || '',
        } : {}}
        recipient={whatsappPanelCompany ? {
          phone: getWhatsAppNumber(whatsappPanelCompany),
          phoneSource: 'phone',
          name: whatsappPanelCompany.companyName || '',
        } : null}
      />
    </div>
  );
};

export default Company;